import { fmtFecha } from "@/lib/format";

export function MiniTimelineHoras({ porHora, horas, color, tramos, onTramoClick }) {
  const max = Math.max(1, ...horas.map((h) => porHora[String(h)] || 0));
  return (
    <div className="space-y-1">
      <div className="flex h-8 items-end gap-px">
        {horas.map((h) => {
          const n = porHora[String(h)] || 0;
          const pct = n ? Math.max(20, (n / max) * 100) : 8;
          return (
            <div
              key={h}
              title={`${String(h).padStart(2, "0")}:00 — ${n} persona${n !== 1 ? "s" : ""}`}
              className="flex-1 rounded-t-sm transition-all"
              style={{
                height: `${pct}%`,
                minHeight: n ? 4 : 2,
                backgroundColor: n ? color : "#e4e4e7",
                opacity: n ? 0.55 + (n / max) * 0.45 : 1,
              }}
            />
          );
        })}
      </div>
      <div className="flex justify-between text-[7px] text-zinc-400">
        <span>{horas[0]}h</span>
        <span>{horas[Math.floor(horas.length / 2)]}h</span>
        <span>{horas[horas.length - 1]}h</span>
      </div>
      {tramos?.length > 0 && (
        <div className="mt-1 max-h-16 space-y-0.5 overflow-y-auto">
          {tramos.map((t) => (
            <button
              key={t.id}
              type="button"
              className="block w-full truncate rounded px-0.5 py-0.5 text-left text-[8px] text-zinc-600 hover:bg-fuchsia-50 hover:text-fuchsia-700"
              title={`${t.usuario} ${t.hora_inicio}–${t.hora_fin} — clic para editar`}
              onClick={(e) => {
                e.stopPropagation();
                onTramoClick?.(t);
              }}
            >
              <strong>{t.hora_inicio.slice(0, 5)}–{t.hora_fin.slice(0, 5)}</strong> {t.usuario}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function CoberturaZonasGrid({ dias, horas, coberturaZonas, resumenLabel = "asign.", onCellClick, onTramoClick }) {
  if (!coberturaZonas?.length) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-300 bg-white p-4 text-center text-xs text-zinc-400">
        No hay zonas definidas. Crea zonas en el catálogo para planificar coberturas.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white shadow-sm">
      <div className="border-b border-zinc-100 px-4 py-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Cobertura por zona y horario</p>
        <p className="text-[10px] text-zinc-400">
          Clic en una celda para asignar — personas por franja horaria
        </p>
      </div>
      <table className="w-full min-w-[900px] border-collapse text-xs">
        <thead>
          <tr className="border-b border-zinc-100 bg-zinc-50/80">
            <th className="sticky left-0 z-10 min-w-32 bg-zinc-50/80 px-3 py-2 text-left text-[10px] font-semibold text-zinc-600">
              Zona
            </th>
            {dias.map((d) => (
              <th key={d.fecha} className="min-w-[8.5rem] px-2 py-2 text-center">
                <span className="block text-[10px] font-bold text-violet-700">{d.nombre}</span>
                <span className="text-[9px] text-zinc-400">{fmtFecha(d.fecha).slice(0, 5)}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {coberturaZonas.map((z) => (
            <tr key={z.id} className="border-b border-zinc-50 hover:bg-zinc-50/40">
              <td className="sticky left-0 z-10 border-r border-zinc-100 bg-white px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 shrink-0 rounded" style={{ backgroundColor: z.color }} />
                  <div>
                    <p className="font-semibold text-zinc-800">{z.nombre}</p>
                    <p className="text-[9px] text-zinc-400">
                      {Object.values(z.dias || {}).reduce((s, d) => s + (d.personas || 0), 0)} {resumenLabel}
                    </p>
                  </div>
                </div>
              </td>
              {dias.map((d) => {
                const info = z.dias?.[d.fecha] || { por_hora: {}, tramos: [], personas: 0, total_tramos: 0 };
                const vacio = !info.total_tramos;
                const clickable = !!onCellClick;
                const inner = (
                  <>
                    <div className="mb-1 flex items-center justify-between gap-1">
                      <span className={`text-[10px] font-semibold ${info.personas ? "text-emerald-700" : "text-zinc-400"}`}>
                        {info.personas} pers.
                      </span>
                      {info.total_tramos > 0 && (
                        <span className="text-[9px] text-zinc-400">
                          {info.total_tramos} tramo{info.total_tramos !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    {vacio ? (
                      <p className="py-2 text-center text-[9px] text-fuchsia-500">Sin cubrir — clic para asignar</p>
                    ) : (
                      <>
                        <MiniTimelineHoras
                          porHora={info.por_hora}
                          horas={horas}
                          color={z.color}
                          tramos={info.tramos}
                          onTramoClick={onTramoClick}
                        />
                        <p className="mt-1 text-center text-[8px] font-medium text-fuchsia-500">+ Clic para añadir otro</p>
                      </>
                    )}
                  </>
                );
                return (
                  <td key={d.fecha} className="align-top px-2 py-2">
                    {clickable ? (
                      <button
                        type="button"
                        title={`Asignar en ${z.nombre} — ${fmtFecha(d.fecha)}`}
                        onClick={() => onCellClick(z, d.fecha, info)}
                        className={`w-full rounded-lg border p-2 text-left transition-all hover:border-fuchsia-300 hover:bg-fuchsia-50/40 hover:ring-2 hover:ring-fuchsia-200/80 ${
                          vacio ? "border-dashed border-zinc-200 bg-zinc-50/50" : "border-zinc-200 bg-white"
                        }`}
                      >
                        {inner}
                      </button>
                    ) : (
                      <div
                        className={`rounded-lg border p-2 ${vacio ? "border-dashed border-zinc-200 bg-zinc-50/50" : "border-zinc-200 bg-white"}`}
                      >
                        {inner}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function sugerirNuevoTramo(tramos = [], zonas = []) {
  let horaInicio = "09:00";
  let horaFin = "14:00";
  let zonaId = zonas[0] ? String(zonas[0].id) : "";

  if (tramos.length) {
    const ordenados = [...tramos].sort((a, b) => a.hora_fin.localeCompare(b.hora_fin));
    const ultimo = ordenados[ordenados.length - 1];
    horaInicio = ultimo.hora_fin.slice(0, 5);
    const [h, m] = horaInicio.split(":").map(Number);
    const finH = Math.min(h + 4, 24);
    horaFin = finH >= 24 ? "23:59" : `${String(finH).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

    const usadas = new Set(tramos.map((t) => String(t.zona_id)));
    const otra = zonas.find((z) => !usadas.has(String(z.id)));
    if (otra) zonaId = String(otra.id);
    else if (ultimo.zona_id) zonaId = String(ultimo.zona_id);
  }

  return { horaInicio, horaFin, zonaId };
}

export function inicialesZona(nombre) {
  return (nombre || "?").split(" ").filter(Boolean).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

export function CeldaZona({ dia, emp, onTramoClick, onAdd, fecha }) {
  const ausencia = dia?.ausencia ?? emp.horario?.ausencia;
  const laborable = dia?.laborable ?? emp.horario?.activo;
  const tramos = dia?.tramos ?? emp.tramos ?? [];
  const borrador = dia?.borrador ?? tramos.some((t) => !t.publicada);
  const f = fecha ?? dia?.fecha ?? emp.fecha;

  if (ausencia) {
    return (
      <div className="rounded-lg border border-orange-200 bg-orange-50 px-1.5 py-2 text-center text-[9px] font-medium text-orange-800">
        {ausencia.label}
      </div>
    );
  }
  if (!laborable) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-zinc-100 px-1.5 py-2 text-center text-[9px] text-zinc-500">
        Descanso
      </div>
    );
  }
  if (tramos.length) {
    return (
      <div className={`space-y-1 rounded-lg border p-1 ${borrador ? "border-amber-300 bg-amber-50/50" : "border-zinc-200"}`}>
        {tramos.map((t) => (
          <button
            key={t.id}
            type="button"
            className="block w-full rounded px-1 py-0.5 text-left text-[9px] font-medium transition-colors hover:bg-white/60"
            style={{ borderLeft: `3px solid ${t.zona_color}`, backgroundColor: `${t.zona_color}15` }}
            onClick={() => onTramoClick(emp, t, f)}
          >
            <span style={{ color: t.zona_color }}>{t.zona_nombre}</span>
            <span className="block text-zinc-500">{t.hora_inicio}–{t.hora_fin}</span>
            {!t.publicada && <span className="text-amber-600">· borrador</span>}
          </button>
        ))}
        <button
          type="button"
          className="w-full rounded border border-dashed border-fuchsia-200 px-1 py-1 text-center text-[8px] font-medium text-fuchsia-600 transition-colors hover:border-fuchsia-300 hover:bg-fuchsia-50/60"
          onClick={() => onAdd(emp, f)}
        >
          + Añadir tramo
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      className="w-full rounded-lg border border-dashed border-zinc-200 px-1 py-3 text-center text-[9px] text-zinc-400 transition-colors hover:border-fuchsia-300 hover:bg-fuchsia-50/50 hover:text-fuchsia-600"
      onClick={() => onAdd(emp, f)}
    >
      Sin asignar — clic
    </button>
  );
}
